/*
 * The second-pair picker loads the full published catalog on demand. It is
 * registered globally so cart fragments work even after an initially empty cart.
 * Shopify remains responsible for inventory and the cheaper-pair discount.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
import { BOGO_CLAIM_PROPERTY, BOGO_ROLE_FREE, BOGO_ROLE_PROPERTY, offerState, refusalFor, shortId } from '@theme/offer-mode';
import { bogoQuote, nextDiscountCodes, overchargeAmount } from '@theme/bogo-pairing';

import { loadBogoCatalog } from './bogo-catalog.js';

const decoder = document.createElement('textarea');
// One invitation for the first pair, rather than another popup on every cart morph.
const promptedPairs = new Set();
document.addEventListener(ThemeEvents.cartUpdate, event => {
  if (event.detail?.resource?.item_count === 0) promptedPairs.clear();
});

/**
 * The `t` filter HTML-escapes its output, and these strings are written with
 * textContent. Harvested from the other offer pickers.
 *
 * @template T
 * @param {T} value
 * @returns {T}
 */
function decodeEntities(value) {
  if (typeof value === 'string') {
    decoder.innerHTML = value;
    return /** @type {T} */ (decoder.value);
  }
  if (value && typeof value === 'object') {
    return /** @type {T} */ (Object.fromEntries(Object.entries(value).map(([k, v]) => [k, decodeEntities(v)])));
  }
  return value;
}

/** The storefront's locale root, e.g. '' or '/en-in', taken from the cart route. */
function localeRoot() {
  const cart = (Theme.routes && Theme.routes.cart_url) || '/cart';
  return cart.replace(/\/cart\/?$/, '');
}

class BogoFreeOffer extends HTMLElement {
  /** @type {Array<object> | null} */
  #candidates = null;

  #loading = null;

  #busy = false;
  #drawer = null;
  #promptFrame = null;

  static observedAttributes = ['data-prompt-key'];
  attributeChangedCallback() {
    if (this.isConnected) this.#schedulePrompt();
  }

  #schedulePrompt = () => {
    cancelAnimationFrame(this.#promptFrame);
    this.#promptFrame = requestAnimationFrame(() => this.#showPrompt());
  };

  #showPrompt() {
    if (!this.isConnected || this.#busy) return;
    const prompt = this.querySelector('[data-bogo-free-prompt]');
    // Only a deliberate cart entry (or the dedicated cart page) may invite.
    if (this.#drawer) {
      if (!this.#drawer.hasAttribute('data-customer-cart-entry')) return;
    } else {
      const cartPath = new URL(Theme.routes.cart_url, window.location.origin).pathname.replace(/\/$/, '');
      if (window.location.pathname.replace(/\/$/, '') !== cartPath) return;
    }
    const enclosingDialog = this.closest('dialog');
    if (enclosingDialog && !enclosingDialog.open) return;
    if (!this.getClientRects().length) return;
    const key = this.dataset.promptKey || this.dataset.paidTitle;
    if (!key || promptedPairs.has(key) || !(prompt instanceof HTMLElement)) return;
    // A customer who already opened the selector does not need the invitation.
    if (this.#panel && !this.#panel.hidden) return;
    prompt.classList.add('is-celebrating');
    promptedPairs.add(key);
  }

  #onPromptClick = event => {
    event.stopPropagation(); // Dismissing the notice must not close the cart.
    if (!(event.target instanceof Element)) return;
    const prompt = this.querySelector('[data-bogo-free-prompt]');
    if (!(prompt instanceof HTMLElement)) return;
    if (event.target.closest('[data-bogo-free-open]')) this.#toggle();
  };


  #onCartUpdate = () => { this.#bindPrompt(); this.#schedulePrompt(); };

  #bindPrompt() {
    const prompt = this.querySelector('[data-bogo-free-prompt]');
    prompt?.addEventListener('click', this.#onPromptClick);
  }

  connectedCallback() {
    this.code = this.dataset.code || '';
    this.paidPrice = Number(this.dataset.paidPrice) || 0;


    try {
      this.messages = decodeEntities(JSON.parse(this.querySelector('[data-bogo-free-messages]')?.textContent || '{}'));
    } catch {
      this.messages = {};
    }

    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onSearch);
    this.#drawer = this.closest('cart-drawer-component');
    this.#drawer?.addEventListener('dialog:open', this.#schedulePrompt);
    this.#drawer?.addEventListener('cart:entered', this.#schedulePrompt);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#bindPrompt();
    this.#schedulePrompt();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onSearch);
    this.#drawer?.removeEventListener('dialog:open', this.#schedulePrompt);
    this.#drawer?.removeEventListener('cart:entered', this.#schedulePrompt);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    cancelAnimationFrame(this.#promptFrame);
  }

  #onSearch = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-bogo-free-search]')) {
      this.#filterCandidates();
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('[data-bogo-free-prompt]')) return;
    if (target.closest('[data-bogo-free-open]')) return this.#toggle();
    if (target.closest('[data-bogo-free-retry]')) return this.#ensureCandidates();
    const add = target.closest('[data-bogo-free-add]');
    if (add instanceof HTMLButtonElement) return this.#claim(add);
  };

  get #panel() {
    return this.querySelector('[data-bogo-free-panel]');
  }

  #setStatus(message) {
    const el = this.querySelector('[data-bogo-free-status]');
    if (el) el.textContent = message || '';
  }

  async #toggle() {
    const panel = this.#panel;
    const button = this.querySelector('[data-bogo-free-open]');
    if (!(panel instanceof HTMLElement)) return;

    const open = panel.hidden;
    panel.hidden = !open;
    if (button instanceof HTMLElement) {
      this.openLabel ??= button.dataset.openLabel || button.textContent;
      button.textContent = (open ? this.messages.close : this.openLabel) || 'Choose your second pair';
      button.setAttribute('aria-expanded', String(open));
    }
    if (open) await this.#ensureCandidates();
  }

  async #ensureCandidates() {
    const retry = this.querySelector('[data-bogo-free-retry]');
    if (retry) retry.hidden = true;
    // Cart morphs can replace the grid but preserve this custom element and cache.
    if (this.#candidates) {
      if (!this.querySelector('[data-bogo-product-title]')) this.#renderCandidates();
      else this.#filterCandidates();
      return;
    }
    this.#setStatus(this.messages.loading || 'Loading products…');
    this.#panel?.setAttribute('aria-busy', 'true');
    try {
      this.#loading ??= loadBogoCatalog({
        root: localeRoot(),
        convertPrice: price => convertMoneyToMinorUnits(price, Theme.currency || 'INR'),
      });
      this.#candidates = await this.#loading;
      if (this.isConnected) this.#renderCandidates();
    } catch (error) {
      // Do not cache failed requests as an empty catalog: the next click retries.
      console.error('bogo-free: could not load products', error);
      this.#candidates = null;
      this.#setStatus('Products could not load. Please try again.');
      if (retry) retry.hidden = false;
    } finally {
      this.#loading = null;
      this.#panel?.removeAttribute('aria-busy');
    }
  }

  #filterCandidates() {
    const query = (this.querySelector('[data-bogo-free-search]')?.value || '').trim().toLowerCase();
    let count = 0;
    this.querySelectorAll('[data-bogo-product-title]').forEach(card => {
      card.hidden = !card.dataset.bogoProductTitle.includes(query);
      if (!card.hidden) count++;
    });
    this.#setStatus(count ? `${count} products` : this.messages.empty || 'No products found.');
  }

  #renderCandidates() {
    const grid = this.querySelector('[data-bogo-free-grid]');
    if (!(grid instanceof HTMLElement)) return;
    grid.replaceChildren();

    for (const product of this.#candidates ?? []) {
      const card = document.createElement('li');
      card.className = 'bogo-free__card';
      card.dataset.bogoProductTitle = product.title.toLowerCase();

      const productUrl = new URL(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`, window.location.origin);
      productUrl.searchParams.set('variant', String(product.variants[0].id));
      if (product.image) {
        const media = document.createElement('a');
        media.href = productUrl.href;
        media.setAttribute('aria-label', `View ${product.title}`);
        media.className = 'bogo-free__media';
        const img = document.createElement('img');
        const imageUrl = new URL(product.image, window.location.origin);
        imageUrl.searchParams.set('width', '240');
        img.src = imageUrl.href;
        img.alt = product.title;
        img.loading = 'lazy';
        media.append(img);
        card.append(media);
      }

      const title = document.createElement('a');
      title.href = productUrl.href;
      title.className = 'bogo-free__title';
      title.textContent = product.title;
      card.append(title);

      // Its own price, plainly. Not struck through and not labelled "free": which
      // of the two pairs Shopify frees depends on which is cheaper, and the rule
      // is stated once above the strip instead of promised per card.
      const price = document.createElement('span');
      price.className = 'bogo-free__price';
      price.textContent = formatMoney(
        product.variants[0].price,
        Theme.moneyFormat || '{{amount}}',
        Theme.currency || 'INR'
      );
      card.append(price);

      let select = null;
      if (product.variants.length > 1) {
        select = document.createElement('select');
        select.className = 'bogo-free__variant';
        select.setAttribute('aria-label', `${this.messages.selectSize ?? 'Select size'} — ${product.title}`);
        for (const variant of product.variants) {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = variant.title + (!variant.available ? ' — Sold out' : variant.price <= 0 ? ' — Unavailable for this offer' : '');
          option.disabled = !variant.available || variant.price <= 0;
          select.append(option);
        }
        card.append(select);
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button bogo-free__add';
      button.dataset.bogoFreeAdd = String(product.variants[0].id);
      button.disabled = !product.variants[0].available || product.variants[0].price <= 0;
      button.textContent = button.disabled ? (this.messages.soldOut || 'Unavailable') : (this.messages.add || 'Select second pair');
      card.append(button);

      if (select) {
        select.addEventListener('change', () => {
          button.dataset.bogoFreeAdd = select.value;
          productUrl.searchParams.set('variant', select.value);
          card.querySelectorAll('a').forEach(link => { link.href = productUrl.href; });
          const selected = product.variants.find(variant => String(variant.id) === select.value);
          price.textContent = formatMoney(selected.price, Theme.moneyFormat || '{{amount}}', Theme.currency || 'INR');
        });
      }

      grid.append(card);
    }
    this.#filterCandidates();
  }

  /** Fresh cart read. Throws on anything that is not a readable cart, so callers fail closed. */
  async #readCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`bogo-free: /cart.js answered ${response.status}`);
    const cart = await response.json();
    if (!cart || !Array.isArray(cart.items)) throw new Error('bogo-free: /cart.js returned no items array');
    return cart;
  }

  /** @returns {string[]} */
  #cartItemsSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);
  }

  /**
   * Applies the configured code, keeping every code the customer already has -
   * /cart/update.js `discount` replaces the whole set rather than appending.
   *
   * @param {object} cart - the pre-flight read
   * @param {string[]} sectionIds
   */
  async #applyDiscount(cart, sectionIds) {
    const codes = nextDiscountCodes(cart.discount_codes, this.code, 'apply');
    if (!codes) return null;

    const body = { discount: codes.join(',') };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`bogo-free: /cart/update answered ${response.status}`);
    return response.json();
  }

  /** @param {HTMLButtonElement} button */
  async #claim(button) {
    if (this.#busy) return;
    this.code = this.dataset.code || '';
    const variantId = Number(button.dataset.bogoFreeAdd);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    const variant = (this.#candidates ?? [])
      .flatMap((product) => product.variants)
      .find((v) => v.id === variantId);

    if (!variant || !variant.available || variant.price <= 0) {
      this.#setStatus(this.messages.error ?? '');
      return;
    }

    this.#busy = true;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.messages.adding ?? '';
    this.#setStatus('');

    try {
      // Pre-flight AND the before-total, read first and together: byob-gift-sync
      // reacts to the cart:update dispatched below and its gift lines would
      // corrupt a later comparison.
      const before = await this.#readCart();
      const refusal = refusalFor('bogo', offerState(before, this.code));
      if (refusal) {
        this.#setStatus(this.messages.claimExists ?? '');
        return;
      }
      const paidItems = before.items.filter(item => !item.properties?._bogo_claim && !item.properties?._byob_gift_type);
      if (!paidItems.length) {
        this.#setStatus('Your cart changed. Add your first pair before choosing a second pair.');
        return;
      }
      this.paidPrice = Math.max(...paidItems.map(item => Number(item.original_price ?? item.price) || 0));
      const totalBefore = Number(before.total_price);

      const sectionIds = this.#cartItemsSectionIds();
      const payload = {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { [BOGO_CLAIM_PROPERTY]: shortId(), [BOGO_ROLE_PROPERTY]: BOGO_ROLE_FREE },
          },
        ],
      };
      if (sectionIds.length) payload.sections = sectionIds.join(',');

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        this.#setStatus(data.description || data.message || this.messages.error || '');
        return;
      }

      let cart = null;
      let sections = data.sections;
      try {
        const updated = await this.#applyDiscount(before, sectionIds);
        if (updated) {
          cart = updated;
          sections = updated.sections ?? sections;
        }
      } catch (error) {
        // The pair is in the cart; a failed discount must not read as a failed add.
        console.error('bogo-free: could not apply the discount code', error);
      }

      if (!cart || !Array.isArray(cart.items)) cart = await this.#readCart();

      // bogoQuote().total is max(paid, picked) - what the customer pays once the
      // cheaper of the two is free. So the cart should rise by that minus what is
      // already in it: zero for a cheaper pick, the difference for a dearer one.
      const expected = bogoQuote(this.paidPrice, variant.price).total - this.paidPrice;
      const overcharge = overchargeAmount(
        Number.isFinite(totalBefore) ? totalBefore : null,
        Number(cart.total_price),
        expected
      );
      if (overcharge > 0) {
        console.error(
          `bogo-free: the free pair was not discounted. Expected the cart to rise by ${expected} but it rose by ` +
            `${Number(cart.total_price) - totalBefore} (minor units) - ${overcharge} too much.\n` +
            `Check the "${this.code}" Buy X Get Y code: it must cover both products' collections, give 1 item at ` +
            '100% off, and allow 1 use per order.'
        );
        this.#setStatus(this.messages.notDiscounted ?? this.messages.error ?? '');
      }

      (this.isConnected ? this : document).dispatchEvent(
        new CartAddEvent(cart, this.id || 'bogo-free', { source: 'bogo-free', itemCount: 1, sections })
      );
    } catch (error) {
      console.error(error);
      this.#setStatus(this.messages.error ?? '');
    } finally {
      this.#busy = false;
      button.disabled = false;
      button.textContent = label ?? '';
    }
  }
}

if (!customElements.get('bogo-free-offer')) {
  customElements.define('bogo-free-offer', BogoFreeOffer);
}
